import { useDispatch } from 'react-redux';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import * as yup from 'yup';
import AddIcon from '@mui/icons-material/Add';
import { Typography } from '@components/Typography';
import { Input } from '@components/Input';
import { alertActions, accountActions } from '@actions';
import { ifoodIntegrationService } from '@services';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { handleApiError } from '@helpers/api';
import { DeleteIcon } from 'assets/images/icons';
import { Modal, Content, LocationWrapper, StyledIconButton } from './styles';

const IFoodModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const IFoodSchema = yup.object().shape({
        cnpj: yup
            .array()
            .min(1, 'validation.required')
            .of(
                yup.object().shape({
                    id: yup.string().required('validation.required'),
                }),
            ),
    });

    const resolver = useYupValidationResolver(IFoodSchema);

    const {
        control,
        formState: { isValid, isSubmitting },
        getValues,
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            cnpj: [{ id: '' }],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'cnpj',
    });

    const onConnectIFood = async () => {
        const values = getValues();
        try {
            await ifoodIntegrationService.connectIntegration({
                cnpj: values.cnpj.map(location => location.id),
            });
            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
            onClose();
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            handleClose={onClose}
            title={t('SettingsIntegrations.iFoodTitle')}
            subtitle={
                <Typography variant="caption" color="textSecondary">
                    {t('SettingsIntegrations.iFoodSubtitle')}
                </Typography>
            }
            onPrimaryAction={onConnectIFood}
            primaryActionText={t('buttons.connect')}
            primaryActionDisabled={isSubmitting || !isValid}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
        >
            <Content>
                <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '8px' }}>
                    {t('SettingsIntegrations.iFoodInstructions')}
                </Typography>
                <LocationWrapper>
                    <div style={{ display: 'flex', gap: '8px', flexFlow: 'column-reverse' }}>
                        {fields.map((field, index) => {
                            return (
                                <div style={{ position: 'relative' }} key={`cnpj-${index}-${field.id || 'empty'}`}>
                                    <Controller
                                        control={control}
                                        name={`cnpj.${index}.id`}
                                        render={({ field, fieldState: { error }, ref }) => (
                                            <Input
                                                ref={ref}
                                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                                label={'CNPJ'}
                                                fullWidth
                                                error={!!error}
                                                helperText={error ? t(error.message) : null}
                                                required
                                            />
                                        )}
                                    />
                                    {fields.length - 1 === index ? (
                                        <StyledIconButton
                                            sx={{ right: '-42px', top: '3px' }}
                                            onClick={() => append({ id: '' })}
                                        >
                                            <AddIcon />
                                        </StyledIconButton>
                                    ) : (
                                        <StyledIconButton onClick={() => remove(index)}>
                                            <DeleteIcon />
                                        </StyledIconButton>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </LocationWrapper>
            </Content>
        </Modal>
    );
};

export default IFoodModal;
